import { type FC } from "react";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { PillBannerButton } from "components/footer/PillBannerButton";
import { type PresentationBannerColorType } from "components/footer/PresentationBanner";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectClassSessionBySectionId, selectCurrentSectionId } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { Link } from "utils/routing";
import { pageUrl, withQueryParams } from "utils/urls";

interface FollowButtonProps {
  color: PresentationBannerColorType;
  sectionId?: string;
  showReturnText?: boolean;
}

export const FollowButton: FC<FollowButtonProps> = ({
  color,
  showReturnText = false,
  sectionId: sectionIdProp,
}) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const sectionId = sectionIdProp || currentSectionId || "";
  const classSession = useAppSelector((state) => selectClassSessionBySectionId(state, sectionId));
  const presentedPageId = classSession?.presented_page_id;
  const showShortText = !useIsMdOrLarger();
  const { canParticipateInSection } = useSectionCapabilitiesBySectionId(sectionId);
  const dispatch = useAppDispatch();

  if (!sectionId || !presentedPageId) {
    return null;
  }

  const presentedPageURL = withQueryParams(pageUrl(sectionId, presentedPageId), {
    [FOLLOWING_QUERY_PARAM]: "1",
  });

  const closeSidebar = () => {
    if (canParticipateInSection) {
      dispatch(localStateActions.setIsSidebarOpen(false));
    }
  };

  return (
    <PillBannerButton
      as={Link}
      eventName="Presentation - Follow along"
      kind={`banner-${color}`}
      to={presentedPageURL}
      onClick={closeSidebar}
    >
      {showReturnText
        ? t("common.return")
        : showShortText
          ? t("main_header.follow_short")
          : t("main_header.follow")}
    </PillBannerButton>
  );
};
